<template>
    <div class="card mb-4">
        <div class="card-header">
            <h1 class="card-title">{{ $t('invite.panelTitle') }}<span class="float-right">
                <small><span class="red">*</span> = {{ $t('required') }}</small>
            </span></h1>
        </div>

        <div class="card-body">
            <p v-html="$t('invite.intro', {
                pending: pendingInvites,
                max: maxInvites,
                interpolation: { escapeValue: false }
                })">
            </p>
            <div v-if="this.pendingInvites >= this.maxInvites" class="card-body">
                <div class="alert alert-warning">
                    {{ $t('invite.maxExceeded') }}
                </div>
            </div>
            <template v-else>
                <table class="table mb-1">
                    <thead>
                    <tr class="text-left">
                        <th>{{ $t('invite.columns.title') }}</th>
                        <th>{{ $t('invite.columns.firstName') }} <span class="red">*</span></th>
                        <th>{{ $t('invite.columns.lastName') }} <span class="red">*</span></th>
                        <th>{{ $t('invite.columns.email') }} <span class="red">*</span></th>
                        <th>{{ $t('invite.columns.role') }}</th>
                        <th></th>
                    </tr>
                    </thead>
                    <tbody ref="container">
                    <new-agent-row v-for="agent in agents"
                                   @agentRemoved="removeAgent"
                                   :agent="agent"
                                   :titles="titles"
                                   ref="agentRows"
                                   :key="agent.id"
                                   :agents="agents"></new-agent-row>
                    </tbody>
                </table>
                <div class="clearfix">
                    <button v-if="canAddAgent"
                            type="button"
                            @click="addAgent()"
                            class="btn btn-info btn-sm"
                            :title="$t('invite.addAgentDescription')">
                        <i class="fa fa-plus-circle"></i>
                        {{ $t('invite.addAgent') }}
                    </button>
                    <b-btn v-if="canAddAgent"
                           v-b-modal.csvUploadModal
                           class="btn btn-info mb-0 btn-sm"
                           :title="$t('invite.uploadCsvDescription')">
                        <i class="fa fa-upload" aria-hidden="true"></i>
                        {{ $t('invite.uploadCsv') }}
                    </b-btn>

                    <button type="button" @click="deleteAllAgents"
                            class="btn btn-danger btn-sm pull-right"
                            :title="$t('invite.clearAllDescription')">
                        <i class="fa fa-trash" aria-hidden="true"></i>
                        {{ $t('invite.clearAll') }}
                    </button>
                </div>
            </template>
            <div id="responseMessageDiv" class="alert mt-4 mb-0"
                 :class="'alert-' + responseMessageType"
                 v-if="responseMessages.length > 0">
                <ul id="responseMessageList" class="list-unstyled mb-0">
                    <li v-for="message in responseMessages">{{ message }}</li>
                </ul>
            </div>
        </div>
        <div class="card-footer text-center">
            <div v-if="isSubmitting"
                 class="btn btn-primary disabled">
                <div>
                    <i class="fa fa-spinner fa-spin" aria-hidden="true"></i> {{ $t('invite.submitting') }}
                </div>
            </div>
            <button v-else
                    :disabled="this.pendingInvites >= this.maxInvites"
                    type="button"
                    @click="sendInvites"
                    class="btn btn-primary">
                <i class="fa fa-envelope" aria-hidden="true"></i>
                {{ $t('invite.submit') }}
            </button>
        </div>

        <!-- Modal Component -->
        <b-modal id="csvUploadModal" ref="csvUploadModal" :title="$t('invite.csvModal.modalTitle')" :hide-footer="true">
            <p v-html="$t('invite.csvModal.label', {interpolation: {escape: function escape(str) { return str; }}})"></p>
            <b-form-file
                    ref="csvInput"
                    id="csv-upload-field"
                    @change="handleCsvUpload"
                    accept=".csv, text/csv"
                    :placeholder=" $t('invite.csvModal.placeholder')"
                    class="mb-4">
            </b-form-file>
            <hr>
            <div class="text-muted" v-html="$t('invite.csvModal.explanation')"></div>
        </b-modal>
    </div>
</template>

<script>
    import NewAgentRow from './NewAgentRow';
    import Qs from 'qs';

    export default {
        name: 'new-agent-list',
        components: {NewAgentRow},
        props: ['titles', 'pendingInvites', 'maxInvites'],
        data() {
            return {
                agents: [],
                responseMessages: [],
                uploadedFile: '',
                isSubmitting: false,
                responseMessageType: 'danger',
                emailCounts: [],
                idIndex: 0,
            };
        },
        computed: {
            canAddAgent() {
                return this.pendingInvites + this.agents.length < this.maxInvites;
            }
        },
        mounted() {
            this.addAgent();
        },
        methods: {
            sendInvites() {
                this.validate();
                if (this.responseMessages.length === 0) {
                    this.isSubmitting = true;
                    axios.post(
                        '/agency/invite-agents',
                        Qs.stringify({agents: this.agents})
                    ).then(this.inviteSuccess);
                }
            },
            inviteSuccess: function(response) {
                this.responseMessageType = response.data.success ? 'success' : 'danger';
                this.responseMessages = response.data.message;
                this.isSubmitting = false;
            },
            validateEmail(email) {
                let re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                return re.test(String(email).toLowerCase());
            },
            validatePrefix(prefix) {
                let re = /^(mr|ms|mrs|dr)\b/;
                return re.test(String(prefix).toLowerCase());
            },
            validateType(type) {
                let re = /^(agent|master)\b/;
                return re.test(String(type).toLowerCase());
            },
            validateName(name) {
                let re = /^[a-zA-Z]+(([',. -][a-zA-Z ])?[a-zA-Z]*)*$/;
                return re.test(String(name).toLowerCase());
            },
            addAgent(prefix = '', firstName = '', lastName = '', email = '', type = 0) {
                if (!this.canAddAgent) {
                    return;
                }
                if (prefix !== '' && prefix !== null) {
                    if (prefix.slice(-1) !== '.') {
                        prefix = prefix + '.';
                    }
                    prefix = prefix.toLowerCase();
                    prefix = prefix.charAt(0).toUpperCase() + prefix.slice(1).toLowerCase();
                }
                console.log(this.titles);

                if (!this.titles[prefix]) {
                    prefix = 'Mr.';
                }

                this.agents.push({
                    id: this.idIndex,
                    prefix: prefix,
                    first_name: firstName,
                    last_name: lastName,
                    email: email,
                    is_master: type,
                });

                this.idIndex++;
            },
            handleCsvUpload(e) {
                this.removeEmptyRows();
                if (window.FileReader) {
                    let file = e.target.files[0];
                    let reader = new FileReader();
                    let filePath = file.value;
                    let allowedExtensions = /(\.csv)$/i;
                    if (!allowedExtensions.exec(filePath)) {
                        reader.onload = this.getAgentsFromCsv;
                        if (file) {
                            reader.readAsText(file);
                        }
                    }
                }
                this.$refs.csvUploadModal.hide();
                this.$refs.csvInput.reset();
            },
            getAgentsFromCsv: function(event) {
                let result = null;

                if (event.target.result.charCodeAt(0) === 0xFEFF) {
                    result = event.target.result.substr(1);
                } else {
                    result = event.target.result;
                }

                let csvval = result.split(/(\r\n|\n)/);

                for (let rowsIndex = 0; rowsIndex < csvval.length; rowsIndex++) {
                    let csvvalue = csvval[rowsIndex].split(',');

                    let agentPrefix = '';
                    let agentFirstName = '';
                    let agentLastName = '';
                    let agentEmail = '';
                    let agentType = 0;

                    let hasPrefix = false;
                    let hasFirstName = false;
                    let hasLastName = false;
                    let hasEmail = false;
                    let hasType = false;

                    for (let columnIndex = 0; columnIndex < csvvalue.length; columnIndex++) {
                        if (this.validateEmail(csvvalue[columnIndex].trim()) && !hasEmail) {
                            agentEmail = csvvalue[columnIndex].trim();
                            hasEmail = true;
                        } else if (this.validatePrefix(csvvalue[columnIndex].trim()) && !hasPrefix) {
                            agentPrefix = csvvalue[columnIndex].trim();
                            hasPrefix = true;
                        } else if (this.validateType(csvvalue[columnIndex].trim()) && !hasType) {
                            agentType = csvvalue[columnIndex].trim().toLowerCase();
                            if (agentType.toLowerCase() === 'agent' || agentType === '0' || (agentType === '' || agentType === null)) {
                                agentType = 0;
                            } else if ((agentType.toLowerCase() === 'master' || agentType.toLowerCase() === 'master agent') || agentType === '1') {
                                agentType = 1;
                            } else {
                                agentType = 0;
                            }
                            hasType = true;
                        } else if (this.validateName(csvvalue[columnIndex].trim()) && !hasFirstName) {
                            agentFirstName = csvvalue[columnIndex].trim();
                            hasFirstName = true;
                        } else if (this.validateName(csvvalue[columnIndex].trim()) && !hasLastName) {
                            agentLastName = csvvalue[columnIndex].trim();
                            hasLastName = true;
                        }
                    }

                    if (hasFirstName || hasLastName || hasEmail) {
                        if (this.canAddAgent) {
                            this.addAgent(
                                agentPrefix.trim(),
                                agentFirstName.trim(),
                                agentLastName.trim(),
                                agentEmail.trim(),
                                agentType
                            );
                        } else {
                            this.responseMessageType = 'warning';
                            this.responseMessages = [i18n.t('invite.csvModal.tooManyAgents')];
                        }
                    }
                }
            },
            removeAgent(agent) {
                this.agents.splice(this.agents.indexOf(agent), 1);
                if (this.agents.length === 0) {
                    this.addAgent();
                }
            },
            deleteAllAgents() {
                this.agents = [];
                this.responseMessages = [];
                this.addAgent();
            },
            removeEmptyRows() {
                for (let i = 0; i < this.agents.length; i++) {
                    if (this.$refs.agentRows[i].isEmpty()) {
                        this.agents.splice(i, 1);
                    }
                }
            },
            validate() {
                this.responseMessages = [];

                let validationHasErrors = false;
                let hasDuplicateEmails = false;

                if (this.agents.length === 0) {
                    this.responseMessages.push(i18n.t('invite.validation.noAgents'));
                    return;
                }

                this.countEmails();

                for (let i = 0; i < this.agents.length; i++) {
                    if (!this.$refs.agentRows[i].validate()) {
                        validationHasErrors = true;
                    }
                    if (this.emailCounts[this.agents[i].email] > 1) {
                        hasDuplicateEmails = true;
                        this.$refs.agentRows[i].setEmailInvalid();
                    }
                }

                if (validationHasErrors) {
                    this.responseMessages.push(i18n.t('invite.validation.invalid'));
                    this.responseMessageType = 'danger';
                }

                if (hasDuplicateEmails) {
                    this.responseMessages.push(i18n.t('invite.validation.duplicateEmail'));
                    this.responseMessageType = 'danger';
                }
            },
            countEmails() {
                this.emailCounts = [];
                for (let i = 0; i < this.agents.length; i++) {
                    if (this.agents[i].email) {
                        this.emailCounts[this.agents[i].email] ? this.emailCounts[this.agents[i].email] += 1 : this.emailCounts[this.agents[i].email] = 1;
                    }
                }
            }
        }
    };
</script>